import { useState } from "preact/hooks";
import { tr } from "../i18n.js";
import { CHANNEL_LABEL, del, get, html, patch, post, put } from "../lib.js";
import { Badge, Field, Loading, Modal, PageHeader, toast, useAction, useForm, useLoad } from "../ui.js";
import { PageGuide } from "../guide.js";
import { TrackedLinks } from "./links.js";
import { MissedCallsCard } from "./missed-calls.js";

const HELP = {
  whatsapp: {
    idLabel: tr("Phone number ID"),
    idHint: tr("Meta Business → WhatsApp Manager → Phone numbers → Phone number ID (not the phone number itself)."),
    tokenHint: tr("A permanent System User access token with whatsapp_business_messaging permission."),
  },
  messenger: { idLabel: tr("Facebook Page ID"), idHint: tr("Page settings → About → Page ID."), tokenHint: tr("Page access token with pages_messaging permission.") },
  instagram: { idLabel: tr("Instagram account ID"), idHint: tr("The Instagram professional account ID linked to your Page."), tokenHint: tr("Page access token with instagram_manage_messages permission.") },
};

const CHANNEL_STATUS = { active: tr("Active"), paused: tr("Paused") };

/**
 * Website chat exists from sign-up so the owner can test the agent, which made it look connected
 * before the snippet was on any site. Until a real visitor writes, it says it is waiting instead.
 */
const channelBadge = (c) =>
  c.status === "active" && c.type === "webchat" && !c.last_customer_chat_at
    ? html`<${Badge} kind="warn">${tr("Waiting for the first chat")}</${Badge}>`
    : html`<${Badge} kind=${c.status === "active" ? "ok" : "muted"}>${CHANNEL_STATUS[c.status] ?? c.status}</${Badge}>`;

/** Splits a translated sentence on {placeholders} so elements (like <code>) can sit inside it in either word order. */
const fill = (s, parts) => s.split(/\{(\w+)\}/).map((x, i) => (i % 2 ? parts[x] : x));

export function ChannelsPage({ t, me }) {
  const [data, reload] = useLoad(() => get(`${t.base}/channels`), [t.id]);
  const [adding, setAdding] = useState(null);
  const [replacing, setReplacing] = useState(null);
  const [numbersOf, setNumbersOf] = useState(null);
  const [askShield, setAskShield] = useState(null);
  const [busy, run] = useAction();
  const webchat = data?.channels.find((c) => c.type === "webchat");
  const snippet = webchat ? `<script src="${location.origin}/widget.js" data-key="${webchat.external_id}" async></script>` : "";

  const toggle = async (c) => {
    await run(() => patch(`${t.base}/channels/${c.id}`, { status: c.status === "active" ? "paused" : "active" }), c.status === "active" ? tr("Channel paused") : tr("Channel resumed"));
    reload();
  };
  const setShield = async (c, on) => {
    await run(() => patch(`${t.base}/channels/${c.id}`, { personal_shield: on }), on ? tr("Personal Shield is on") : tr("Personal Shield is off"));
    reload();
  };

  return html`<div>
    <${PageHeader} title=${tr("Channels")} subtitle=${tr("Where your agent talks to customers")}>
      <button class="btn primary" onClick=${() => setAdding(data?.embedded_signup ? "whatsapp-es" : "whatsapp")}>${tr("Connect WhatsApp")}</button>
      <button class="btn" onClick=${() => setAdding("instagram")}>${CHANNEL_LABEL.instagram}</button>
      <button class="btn" onClick=${() => setAdding("messenger")}>${CHANNEL_LABEL.messenger}</button>
    </${PageHeader}>
    <${PageGuide} id="channels" />
    ${!data && html`<${Loading} />`}
    ${data && html`
      <section class="card">
        <h2>${tr("Your channels")}</h2>
        <table class="table"><tbody>${data.channels.map((c) => html`<tr>
          <td><strong>${CHANNEL_LABEL[c.type]}</strong><div class="muted small">${c.display_name} ${c.type !== "webchat" ? `· ${c.external_id}` : ""}</div>
            ${c.type === "webchat" && c.status === "active" && !c.last_customer_chat_at && html`<div class="muted small">${tr("Not on your site yet: paste the snippet below.")}</div>`}
            ${c.type === "whatsapp" && c.onboarding === "coexistence" && t.role !== "agent" && html`<${ShieldControl} c=${c} busy=${busy} onToggle=${(on) => setShield(c, on)} onNumbers=${() => setNumbersOf(c)} />`}</td>
          <td>${channelBadge(c)}</td>
          <td class="row-actions">
            <button class="btn small" disabled=${busy} onClick=${() => toggle(c)}>${c.status === "active" ? tr("Pause") : tr("Resume")}</button>
            ${c.type !== "webchat" && html`<button class="btn small" onClick=${() => setReplacing(c)}>${tr("Replace token")}</button>`}
            ${c.type !== "webchat" && html`<button class="btn small ghost" onClick=${async () => { if (confirm(tr("Disconnect this channel?"))) { await run(() => del(`${t.base}/channels/${c.id}`), tr("Disconnected")); reload(); } }}>${tr("Disconnect")}</button>`}
          </td>
        </tr>`)}</tbody></table>
      </section>
      <section class="card">
        <h2>${tr("Website chat")}</h2>
        <p class="muted small">${fill(tr("Paste this before {tag} on your site to add a chat bubble answered by your agent."), { tag: html`<code>${"</body>"}</code>` })}</p>
        <pre class="code">${snippet}</pre>
        <button class="btn small" onClick=${() => navigator.clipboard.writeText(snippet).then(() => toast(tr("Copied")))}>${tr("Copy snippet")}</button>
      </section>
      ${webchat && html`<${ChatLinkCard} t=${t} active=${webchat.status === "active"} />`}
      <${BookingPageCard} t=${t} />
      <${TrackedLinks} t=${t} />
      ${t.role !== "agent" && html`<${MissedCallsCard} t=${t} />`}
      ${me?.user.is_platform_admin && html`<section class="card">
        <h2>${tr("Platform webhook")}</h2>
        <p class="small">${tr("Set once in the RELAYZA Meta app, not per business (WhatsApp → Configuration, and the Messenger/Instagram webhooks). Callback URL:")}</p>
        <pre class="code">${data.webhook_url}</pre>
        <p class="muted small">${fill(tr("Use the platform's {token} and subscribe to {messages} and, for numbers that stay on the WhatsApp Business app, {echoes}."), {
          token: html`<code>META_VERIFY_TOKEN</code>`,
          messages: html`<code>messages</code>`,
          echoes: html`<code>smb_message_echoes</code>`,
        })}</p>
      </section>`}`}
    ${adding === "whatsapp-es" && html`<${EmbeddedSignup} t=${t} cfg=${data.embedded_signup} onManual=${() => setAdding("whatsapp")} onClose=${() => setAdding(null)} onSaved=${(ch) => { setAdding(null); reload(); if (ch?.onboarding === "coexistence") setAskShield(ch); }} />`}
    ${askShield && html`<${ShieldQuestion} t=${t} channel=${askShield} onDone=${() => { setAskShield(null); reload(); }} />`}
    ${numbersOf && html`<${KnownNumbers} t=${t} channel=${numbersOf} onClose=${() => { setNumbersOf(null); reload(); }} />`}
    ${adding && adding !== "whatsapp-es" && html`<${ConnectForm} t=${t} type=${adding} onClose=${() => setAdding(null)} onSaved=${() => { setAdding(null); reload(); }} />`}
    ${replacing && html`<${ReplaceTokenForm} t=${t} channel=${replacing} onClose=${() => setReplacing(null)} />`}
  </div>`;
}

// ── Personal Shield: keep the AI out of the owner's personal chats on a coexistence number ──

const SHIELD_LINE = tr("For a number you also use for family and friends: the AI only answers people you haven't saved in the phone and haven't chatted with before, and nothing from your personal chats is stored.");

function ShieldControl({ c, busy, onToggle, onNumbers }) {
  return html`<div class="shield">
    <label class="toggle small"><input type="checkbox" checked=${!!c.personal_shield} disabled=${busy} onChange=${(e) => onToggle(e.target.checked)} /> ${tr("Personal Shield")}</label>
    <div class="muted small">${SHIELD_LINE}</div>
    ${!!c.personal_shield && html`<button class="btn small ghost" onClick=${onNumbers}>${tr("Known numbers")}</button>`}
  </div>`;
}

/** Asked once, right after a coexistence number connects. */
function ShieldQuestion({ t, channel, onDone }) {
  const [busy, run] = useAction();
  const yes = async () => {
    await run(() => patch(`${t.base}/channels/${channel.id}`, { personal_shield: true }), tr("Personal Shield is on"));
    onDone();
  };
  return html`<${Modal} title=${tr("Do you also use this number for personal chats?")} onClose=${onDone}>
    <p class="small">${SHIELD_LINE}</p>
    <p class="muted small">${tr("You can change this any time on this page.")}</p>
    <div class="form-actions">
      <button class="btn primary" disabled=${busy} onClick=${yes}>${tr("Yes, turn on Personal Shield")}</button>
      <button class="btn ghost" disabled=${busy} onClick=${onDone}>${tr("No, it's only for the business")}</button>
    </div>
  </${Modal}>`;
}

const SCOPE_CHOICES = [
  { value: "", label: tr("Auto") },
  { value: "business", label: tr("Business") },
  { value: "personal", label: tr("Personal") },
];

/** Local form for an Israeli number, as owners know it: 972521234567 → 052-123-4567. */
const localNumber = (waId) => (/^972\d{9}$/.test(waId) ? `0${waId.slice(3, 5)}-${waId.slice(5, 8)}-${waId.slice(8)}` : `+${waId}`);

function KnownNumbers({ t, channel, onClose }) {
  const [q, setQ] = useState("");
  const [pages, setPages] = useState(1);
  const [data, reload] = useLoad(() => get(`${t.base}/channels/${channel.id}/known-numbers?q=${encodeURIComponent(q)}&limit=${50 * pages}`), [channel.id, q, pages]);
  const { values, bind, setValues } = useForm({ wa_id: "", scope: "business" });
  const [busy, run] = useAction();
  const setScope = async (wa_id, scope) => {
    await run(() => put(`${t.base}/channels/${channel.id}/number-scope`, { wa_id, scope: scope || null }), tr("Saved"));
    reload();
  };
  const add = async (e) => {
    e.preventDefault();
    await setScope(values.wa_id, values.scope);
    setValues({ wa_id: "", scope: "business" });
  };
  const n = data?.counts;
  return html`<${Modal} title=${tr("Known numbers")} onClose=${onClose}>
    <p class="muted small">${tr("Saved in the phone and earlier chats count as personal. Switch a number to Business to let the AI answer it (a patient you saved, for example), or to Personal to keep the AI away from it.")}</p>
    ${n && html`<p class="small">${tr("{phone_book} from the phone book · {history} from earlier chats · {business} marked Business · {personal} marked Personal", n)}</p>`}
    <${Field} label=${tr("Search by number")}><input type="search" dir="ltr" inputmode="tel" value=${q} onInput=${(e) => { setQ(e.target.value); setPages(1); }} /></${Field}>
    ${!data && html`<${Loading} />`}
    ${data && data.numbers.length === 0 && html`<p class="muted small">${tr("No numbers yet.")}</p>`}
    ${data && data.numbers.length > 0 && html`<table class="table"><tbody>${data.numbers.map((k) => html`<tr>
      <td dir="ltr">${localNumber(k.wa_id)}</td>
      <td class="small muted">${[k.in_phone_book && tr("Phone book"), k.had_history && tr("Earlier chat")].filter(Boolean).join(" · ")}</td>
      <td><select aria-label=${tr("Who this is")} value=${k.scope ?? ""} disabled=${busy} onChange=${(e) => setScope(k.wa_id, e.target.value)}>${SCOPE_CHOICES.map((o) => html`<option value=${o.value}>${o.label}</option>`)}</select></td>
    </tr>`)}</tbody></table>`}
    ${data?.next_offset != null && html`<button class="btn small ghost" onClick=${() => setPages(pages + 1)}>${tr("Show more")}</button>`}
    <form onSubmit=${add}>
      <h3>${tr("Set a number by hand")}</h3>
      <div class="row">
        <${Field} label=${tr("Number")}><input required dir="ltr" inputmode="tel" placeholder="052-123-4567" ...${bind("wa_id")} /></${Field}>
        <${Field} label=${tr("Who this is")}><select ...${bind("scope")}><option value="business">${tr("Business")}</option><option value="personal">${tr("Personal")}</option></select></${Field}>
      </div>
      <button class="btn" disabled=${busy}>${tr("Save")}</button>
    </form>
  </${Modal}>`;
}

// ── Chat Link: the web chat as a page of its own, for a business without WhatsApp Business ──

const CHAT_LINK_VARIANTS = [
  { src: "status", label: tr("WhatsApp status") },
  { src: "profile", label: tr("WhatsApp profile") },
  { src: "bio", label: tr("Instagram bio") },
  { src: "reply", label: tr("Quick replies") },
  { src: "signature", label: tr("Email signature") },
];

function ChatLinkCard({ t, active }) {
  const url = `${location.origin}/chat/${t.slug}`;
  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast(tr("Copied")));
  return html`<section class="card">
    <h2>${tr("Chat Link")}</h2>
    <p class="muted small">${tr("A chat page of its own, answered by your agent. Customers leave their name and mobile, then chat, book and reschedule as they would on WhatsApp. Use it when you can't connect WhatsApp, for example on a personal number.")}</p>
    ${!active && html`<p class="small">${tr("Website chat is paused, so this link shows that the chat isn't available. Resume it above.")}</p>`}
    <pre class="code" dir="ltr">${url}</pre>
    <button class="btn small" onClick=${() => copy(url)}>${tr("Copy link")}</button> <a class="btn small ghost" href=${url} target="_blank" rel="noopener">${tr("Open")}</a>
    <h3>${tr("A link for each place")}</h3>
    <p class="muted small">${tr("Each one records where the customer found you, so Overview shows which place brings chats and bookings.")}</p>
    <table class="table"><tbody>${CHAT_LINK_VARIANTS.map((v) => html`<tr>
      <td><strong>${v.label}</strong><div class="muted small" dir="ltr">${url}?src=${v.src}</div></td>
      <td class="row-actions"><button class="btn small" onClick=${() => copy(`${url}?src=${v.src}`)}>${tr("Copy")}</button></td>
    </tr>`)}</tbody></table>
    <p class="muted small">${tr("Confirmations and reminders appear in the chat. They also go to WhatsApp at the customer's mobile when WhatsApp is connected, otherwise by email.")}</p>
    ${t.sms_available && html`<p class="muted small">${tr("A customer who left only a mobile number gets their confirmation and reminders by SMS when WhatsApp can't reach them.")}</p>`}
  </section>`;
}

// ── Online booking page: a link to share and an iframe for the business's own site ──

function BookingPageCard({ t }) {
  const [busy, run] = useAction();
  const on = !!t.settings?.booking_page_enabled;
  const url = `${location.origin}/book/${t.slug}`;
  const embed = `<iframe src="${url}?embed=1" title="${tr("Book an appointment")}" style="width:100%;max-width:560px;height:720px;border:0" loading="lazy"></iframe>`;
  const copy = (text) => navigator.clipboard.writeText(text).then(() => toast(tr("Copied")));
  const toggle = async (e) => {
    const enabled = e.target.checked;
    await run(() => patch(t.base, { settings: { booking_page_enabled: enabled } }), enabled ? tr("Booking page is live") : tr("Booking page turned off"));
    t.reload();
  };
  return html`<section class="card">
    <div class="section-head"><h2>${tr("Online booking page")}</h2><label class="toggle"><input type="checkbox" checked=${on} disabled=${busy} onChange=${toggle} /> ${tr("Customers can book online")}</label></div>
    <p class="muted small">${tr("A page where customers choose a service, a staff member when more than one provides it, and a free time, then book with their name and mobile. It runs on the same calendar as your agent, so it never offers a time that's taken.")}</p>
    ${on && html`
      <h3>${tr("Link")}</h3>
      <p class="muted small">${tr("Share it in your Instagram bio, your Google Business profile or a WhatsApp status.")}</p>
      <pre class="code" dir="ltr">${url}</pre>
      <button class="btn small" onClick=${() => copy(url)}>${tr("Copy link")}</button> <a class="btn small ghost" href=${url} target="_blank" rel="noopener">${tr("Open")}</a>
      <h3>${tr("Embed on your website")}</h3>
      <p class="muted small">${tr("Paste this where the calendar should appear on your site.")}</p>
      <pre class="code" dir="ltr">${embed}</pre>
      <button class="btn small" onClick=${() => copy(embed)}>${tr("Copy embed code")}</button>
      <p class="muted small">${tr("With WhatsApp connected, customers who book here get their reminders on WhatsApp. Services with a deposit are confirmed once it is paid.")}</p>`}
  </section>`;
}

// ── WhatsApp Embedded Signup (Meta's popup; no ids or tokens to copy) ───────

let fbSdk = null;
function loadFacebookSdk(cfg) {
  fbSdk ??= new Promise((resolve, reject) => {
    window.fbAsyncInit = () => {
      window.FB.init({ appId: cfg.app_id, autoLogAppEvents: true, xfbml: false, version: cfg.graph_version });
      resolve(window.FB);
    };
    const s = document.createElement("script");
    s.src = "https://connect.facebook.net/en_US/sdk.js";
    s.async = true;
    s.crossOrigin = "anonymous";
    s.onerror = () => {
      fbSdk = null;
      reject(new Error(tr("Couldn't load Facebook's sign-in. Check that no blocker is stopping connect.facebook.net.")));
    };
    document.body.appendChild(s);
  });
  return fbSdk;
}

/**
 * Runs Meta's Embedded Signup popup. Meta reports the chosen WABA and number through a
 * window message and the auth code through the login callback, in either order.
 */
function runEmbeddedSignup(FB, cfg, coexistence) {
  return new Promise((resolve, reject) => {
    let info = null;
    let code = null;
    let done = false;
    const finish = (err, value) => {
      if (done) return;
      done = true;
      window.removeEventListener("message", onMessage);
      clearTimeout(timer);
      err ? reject(err) : resolve(value);
    };
    const maybeResolve = () => info && code && finish(null, { ...info, code });
    const onMessage = (event) => {
      if (!/(^|\.)facebook\.com$/.test(new URL(event.origin).hostname)) return;
      let data;
      try {
        data = typeof event.data === "string" ? JSON.parse(event.data) : event.data;
      } catch {
        return;
      }
      if (data?.type !== "WA_EMBEDDED_SIGNUP") return;
      if (String(data.event).startsWith("FINISH")) {
        // v4 can finish with only a WABA (FINISH_ONLY_WABA, FINISH_GRANT_ONLY_API_ACCESS); without a number there's nothing to connect.
        if (!data.data?.phone_number_id) return finish(new Error(tr("Meta finished without a phone number. Run it again and pick or add the number to connect.")));
        info = { waba_id: data.data?.waba_id, phone_number_id: data.data.phone_number_id, business_id: data.data?.business_id ?? null };
        maybeResolve();
      } else if (data.event === "CANCEL") {
        finish(new Error(data.data?.current_step ? tr("Signup was closed at step \"{step}\".", { step: data.data.current_step }) : tr("Signup was closed before it finished.")));
      } else if (data.event === "ERROR") {
        finish(new Error(data.data?.error_message ?? tr("Meta reported an error during signup.")));
      }
    };
    window.addEventListener("message", onMessage);
    // Meta sometimes delivers the session info a moment after the login callback.
    let timer = setTimeout(() => finish(new Error(tr("Signup timed out. Try again."))), 15 * 60_000);
    FB.login(
      (response) => {
        code = response?.authResponse?.code ?? null;
        if (!code) return finish(new Error(tr("Signup was cancelled.")));
        if (!info) {
          clearTimeout(timer);
          timer = setTimeout(() => finish(new Error(tr("Meta didn't report which number you picked. Try again."))), 15_000);
        }
        maybeResolve();
      },
      {
        config_id: cfg.config_id,
        response_type: "code",
        override_default_response_type: true,
        // Embedded Signup v4: the version comes from the login configuration (META_ES_CONFIG_ID must be one
        // created with products selected), so extras carry no version or sessionInfoVersion. Those two
        // keys were the v2/v3 shape Meta retired on 15 October 2026. See docs/meta-versions.md.
        extras: { setup: {}, ...(coexistence ? { featureType: "whatsapp_business_app_onboarding" } : {}) },
      },
    );
  });
}

function EmbeddedSignup({ t, cfg, onManual, onClose, onSaved }) {
  const [coexistence, setCoexistence] = useState(false);
  const [busy, run] = useAction();
  const [error, setError] = useState(null);
  const start = async () => {
    setError(null);
    try {
      const FB = await loadFacebookSdk(cfg);
      const result = await runEmbeddedSignup(FB, cfg, coexistence);
      const res = await run(() => post(`${t.base}/channels/whatsapp/embedded-signup`, { ...result, coexistence }), tr("WhatsApp connected"));
      onSaved(res?.channel);
    } catch (err) {
      setError(err.message ?? String(err));
    }
  };
  return html`<${Modal} title=${tr("Connect WhatsApp")} onClose=${onClose}>
    <p class="small">${tr("Sign in with the Facebook account that manages your business. Meta will walk you through choosing or creating a WhatsApp Business Account and verifying your number with a code.")}</p>
    <label class="toggle"><input type="checkbox" checked=${coexistence} onChange=${(e) => setCoexistence(e.target.checked)} /> ${tr("This number already uses the WhatsApp Business app, and we want to keep using the app")}</label>
    <p class="muted small">${coexistence
      ? tr("Your chat history stays in the app. Replies you send from the phone show up in the inbox and pause the AI on that chat.")
      : tr("The number moves to the WhatsApp Cloud API. If it's active in a WhatsApp app today, it stops working there; tick the box above to keep the WhatsApp Business app.")}</p>
    ${error && html`<div class="callout error small">${error}</div>`}
    <button class="btn primary full" disabled=${busy} onClick=${start}>${busy ? tr("Connecting…") : tr("Continue with Facebook")}</button>
    <p class="muted small center"><button class="link" onClick=${onManual}>${tr("Enter the phone number ID and token by hand instead")}</button></p>
  </${Modal}>`;
}

function ConnectForm({ t, type, onClose, onSaved }) {
  const { values, bind } = useForm({ external_id: "", display_name: "", access_token: "" });
  const [busy, run] = useAction();
  const help = HELP[type];
  const submit = async (e) => {
    e.preventDefault();
    await run(() => post(`${t.base}/channels`, { type, ...values }), tr("{channel} connected", { channel: CHANNEL_LABEL[type] }));
    onSaved();
  };
  return html`<${Modal} title=${tr("Connect {channel}", { channel: CHANNEL_LABEL[type] })} onClose=${onClose}>
    <form onSubmit=${submit}>
      <${Field} label=${help.idLabel} hint=${help.idHint}><input required inputmode="numeric" ...${bind("external_id")} /></${Field}>
      <${Field} label=${tr("Label")}><input placeholder=${tr("e.g. Main line")} ...${bind("display_name")} /></${Field}>
      <${Field} label=${tr("Access token")} hint=${`${help.tokenHint} ${tr("Stored encrypted; never shown again.")}`}><input required type="password" autocomplete="off" ...${bind("access_token")} /></${Field}>
      <button class="btn primary full" disabled=${busy}>${tr("Connect")}</button>
    </form>
  </${Modal}>`;
}

/** Swaps the stored access token (temporary tokens expire; Meta rotates them) without disconnecting, so history is kept. */
function ReplaceTokenForm({ t, channel, onClose }) {
  const { values, bind } = useForm({ access_token: "" });
  const [busy, run] = useAction();
  const help = HELP[channel.type];
  const submit = async (e) => {
    e.preventDefault();
    await run(() => patch(`${t.base}/channels/${channel.id}`, { access_token: values.access_token }), tr("Token replaced"));
    onClose();
  };
  return html`<${Modal} title=${tr("Replace {channel} token", { channel: CHANNEL_LABEL[channel.type] })} onClose=${onClose}>
    <form onSubmit=${submit}>
      <p class="muted small">${channel.display_name || CHANNEL_LABEL[channel.type]} · ${channel.external_id}. ${tr("Conversations and history stay as they are.")}</p>
      <${Field} label=${tr("New access token")} hint=${`${help.tokenHint} ${tr("Stored encrypted; never shown again.")}`}><input required minlength="20" type="password" autocomplete="off" ...${bind("access_token")} /></${Field}>
      <button class="btn primary full" disabled=${busy}>${tr("Save token")}</button>
    </form>
  </${Modal}>`;
}
